import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLazyGetSpacesQuery } from "../../../redux/api/space/spaceAPI";
import { selectBillingSpaceFilter } from "../../../redux/features/billing-space-filter/billing-space-filter-slice";
import { selectSmartControllersFilter } from "../../../redux/features/smart-controllers-filter/smart-controllers-filter-slice";
import { EDeviceTypes } from "../../oversight-core/enums/device-types";
import { ESmartControllerType } from "../../oversight-core/enums/smart-controller-type";
import { AppRoute } from "../../oversight-core/interfaces/app-routes";
import { ISpaceView } from "../../oversight-core/interfaces/entities/space";
import { ISelectedSpace } from "../../oversight-core/interfaces/selected-space";
import FilteringSidePanel from "../../oversight-core/shared-components/filtering-side-panel/filtering-side-panel";
import AppSelect, {
  Option,
} from "../../oversight-core/ui-elements/app-select/app-select";
import AppButton from "../../oversight-core/ui-elements/buttons/app-button/app-button";
import ButtonWithBadge from "../../oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import Search from "../../oversight-core/ui-elements/search/search";
import SpaceSelectorDropdown from "../../oversight-core/ui-elements/space-selector-dropdown/space-selector-dropdown";
import getDeviceTypes, {
  getDeviceLabel,
} from "../../oversight-core/utils/getDeviceType";
import SelectedFiltersView from "../selected-filters-view/selected-filters-view";
import styles from "./smart-controllers-filter.module.scss";

const deviceTypes: Option[] = [
  { value: "", label: getDeviceLabel(EDeviceTypes.ALL_DEVICES, true) },
  ...getDeviceTypes(true),
];

const defaultDeviceType: Option = deviceTypes[0];

interface IProps {
  updateFilters: (
    selectedSpace: ISelectedSpace,
    searchText: string,
    selectedDeviceType: string,
    selectedGroupBy: string,
    selectedSmartDeviceType: string,
    selectedSearchTextType: string,
    badgeValue: number,
    isClear: boolean
  ) => void;
  isLoading: boolean;
}

const groupBy = [
  { label: "Space", value: "Space" },
  { label: "Type", value: "Type" },
  { label: "Power State", value: "Status" },
];

const smartDeviceTypeOptions = [
  { label: "All", value: "" },
  { label: "Smart Plugs", value: ESmartControllerType.SMART_PLUG },
  { label: "AC Controllers", value: ESmartControllerType.AC_CONTROLLER },
];

const controllerOptions: Option[] = [
  {
    value: "Controller",
    label: "Controller",
  },
  {
    value: "Device",
    label: "Device",
  },
];

const SmartControllersFilter = (props: IProps) => {
  const navigate = useNavigate();
  const { updateFilters, isLoading } = props;
  const smartControllersFiltersStore = useSelector(
    selectSmartControllersFilter
  );
  const billingSpaceFilter = useSelector(selectBillingSpaceFilter);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState<ISelectedSpace>({
    ...smartControllersFiltersStore.selectedFilterSpace,
  });
  const [searchText, setSearchText] = useState(
    smartControllersFiltersStore.searchText
  );
  const [selectedDeviceType, setSelectedDeviceType] = useState<Option>({
    ...(deviceTypes.find(
      (deviceType) =>
        deviceType.value === smartControllersFiltersStore.selectedDeviceType
    ) || defaultDeviceType),
  });
  const [selectedGroupBy, setSelectedGroupBy] = useState<Option>({
    ...(groupBy.find(
      (groupByItem) =>
        groupByItem.value === smartControllersFiltersStore.selectedGroupBy
    ) || groupBy[0]),
  });
  const [selectedSmartDeviceType, setSelectedSmartDeviceType] =
    useState<Option>({
      ...(smartDeviceTypeOptions.find(
        (smartDeviceType) =>
          smartDeviceType.value ===
          smartControllersFiltersStore.selectedSmartDeviceType
      ) || smartDeviceTypeOptions[0]),
    });
  const [badgeValue, setBadgeValue] = useState(
    smartControllersFiltersStore.badgeValue
  );
  const [selectedSearchTextType, setSelectedSearchTextType] = useState<Option>({
    ...(controllerOptions.find(
      (controllerOption) =>
        controllerOption.value ===
        smartControllersFiltersStore.selectedSearchTextType
    ) || controllerOptions[0]),
  });
  const [spaceClusters, setSpaceClusters] = useState<ISpaceView[]>([]);

  const [triggerGetSpaces] = useLazyGetSpacesQuery();

  useEffect(() => {
    triggerGetSpaces()
      .unwrap()
      .then((response) => {
        let tempArray: ISpaceView[] = [];
        for (const spaceCluster of response.spaceClusters) {
          tempArray.push({
            ...spaceCluster.rootSpace,
            tariffCode: spaceCluster.serviceProviderAccount.tariffCode,
            accountNumber: spaceCluster.serviceProviderAccount.accountNumber,
            accountNumberLabel: spaceCluster.serviceProviderAccount.label,
            clusterId: spaceCluster.id,
          });
        }
        tempArray = tempArray.sort((a, b) =>
          a.name.trim().localeCompare(b.name.trim())
        );
        setSpaceClusters([...tempArray]);
      })
      .catch(() => {
        setSpaceClusters([]);
      });
  }, []);

  const handleSearchChange = () => {
    updateFilters(
      {
        clusterId: selectedSpace.clusterId,
        id: selectedSpace.id,
        name: selectedSpace.name,
      },
      searchText,
      selectedDeviceType.value,
      selectedGroupBy.value,
      selectedSmartDeviceType.value,
      selectedSearchTextType.value,
      badgeValue,
      true
    );
  };

  useEffect(() => {
    if (
      selectedSpace.id !== billingSpaceFilter.spaceCluster.rootSpace.id &&
      selectedDeviceType.value &&
      selectedSmartDeviceType.value &&
      selectedGroupBy.value !== "Space"
    ) {
      setBadgeValue(4);
    } else if (
      (selectedSpace.id !== billingSpaceFilter.spaceCluster.rootSpace.id &&
        selectedDeviceType.value &&
        selectedSmartDeviceType.value) ||
      (selectedSpace.id !== billingSpaceFilter.spaceCluster.rootSpace.id &&
        selectedGroupBy.value &&
        selectedSmartDeviceType.value)
    ) {
      setBadgeValue(3);
    } else if (
      (selectedSpace.id !== billingSpaceFilter.spaceCluster.rootSpace.id &&
        selectedDeviceType.value) ||
      (selectedSpace.id !== billingSpaceFilter.spaceCluster.rootSpace.id &&
        selectedSmartDeviceType.value) ||
      (selectedSpace.id !== billingSpaceFilter.spaceCluster.rootSpace.id &&
        selectedGroupBy.value !== "Space" &&
        selectedGroupBy.value !== "Type") ||
      (selectedDeviceType.value && selectedGroupBy.value !== "Space") ||
      (selectedSmartDeviceType.value && selectedGroupBy.value !== "Space") ||
      (selectedSmartDeviceType.value && selectedDeviceType.value) ||
      (selectedSpace.clusterId &&
        selectedSpace.id !== billingSpaceFilter.spaceCluster.rootSpace.id &&
        selectedGroupBy.value !== "Space")
    ) {
      setBadgeValue(2);
    } else if (
      selectedSpace.id !== billingSpaceFilter.spaceCluster.rootSpace.id ||
      selectedDeviceType.value ||
      selectedSmartDeviceType.value ||
      selectedGroupBy.value !== "Space"
    ) {
      setBadgeValue(1);
    } else {
      setBadgeValue(0);
    }
  }, [
    selectedSpace.clusterId,
    selectedSpace.id,
    selectedDeviceType.value,
    selectedGroupBy.value,
    selectedSmartDeviceType.value,
  ]);

  useEffect(() => {
    setSelectedSpace(smartControllersFiltersStore.selectedFilterSpace);
  }, [smartControllersFiltersStore.selectedFilterSpace]);

  return (
    <>
      <Row className="align-items-center justify-content-between">
        <Col>
          <Row>
            {/* <Col className="col-5 col-lg-2 pe-0">
              <AppSelect
                selectedValue={selectedSearchTextType}
                options={controllerOptions}
                borderRadiusStyle={"Right"}
                className="font-size-12 font-weight-500 text-light"
                onChangeOption={(selectedOption) => {
                  setSelectedSearchTextType(selectedOption);
                }}
              />
            </Col> */}
            <Col className="col-12 col-sm-6 col-xl-4 col-xxl-3 pe-4 pe-sm-2">
              <Search
                onSearch={handleSearchChange}
                handleSearchChange={(searchText) => {
                  setSearchText(searchText);
                }}
                value={searchText}
                placeholder="Search by Controller Name"
              />
            </Col>
            <Col className="col-auto d-none d-lg-block">
              <AppButton
                text="Add&nbsp;Controller"
                onClick={() => {
                  navigate(AppRoute.ADD_CONTROLLER);
                }}
                size="small"
              />
            </Col>
          </Row>
        </Col>
        <Col className="col-12 col-lg-auto mt-4 mt-lg-0">
          <Row className="align-items-center justify-content-between">
            <Col className="d-block d-lg-none">
              <AppButton
                text="Add&nbsp;Controller"
                onClick={() => {
                  navigate(AppRoute.ADD_CONTROLLER);
                }}
                className="mb-2"
              />
            </Col>
            <Col className={`${styles.FiltersView} col-auto`}>
              <Row className={`${styles.filter} align-items-center`}>
                <Col className="col-auto">
                  <SelectedFiltersView
                    spaceName={smartControllersFiltersStore.selectedSpace.name}
                    filters={[
                      {
                        icon: smartControllersFiltersStore.selectedDeviceType
                          ? (smartControllersFiltersStore.selectedDeviceType as EDeviceTypes)
                          : EDeviceTypes.ALL_DEVICES,
                        filterName:
                          smartControllersFiltersStore.selectedDeviceType ||
                          "All Devices",
                        smartControllerTpe:
                          smartControllersFiltersStore.selectedSmartDeviceType,
                      },
                    ]}
                  />
                </Col>
                <Col className="col-auto">
                  <ButtonWithBadge
                    text="Filter"
                    icon="filter_alt"
                    onClick={() => {
                      setShowFilters(true);
                    }}
                    badgeValue={badgeValue}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <FilteringSidePanel
        isOpen={showFilters && !isLoading}
        onClose={() => setShowFilters(false)}
        onFilter={() => {
          setShowFilters(false);
          updateFilters(
            {
              clusterId: selectedSpace.clusterId,
              id: selectedSpace.id,
              name: selectedSpace.name,
            },
            searchText,
            selectedDeviceType.value,
            selectedGroupBy.value,
            selectedSmartDeviceType.value,
            selectedSearchTextType.value,
            badgeValue,
            true
          );
        }}
        onClearAll={() => {
          if (
            smartControllersFiltersStore.selectedFilterSpace.id !==
              billingSpaceFilter.spaceCluster.rootSpace.id ||
            smartControllersFiltersStore.isClear
          ) {
            updateFilters(
              {
                clusterId: billingSpaceFilter.spaceCluster.id,
                id: billingSpaceFilter.spaceCluster.rootSpace.id,
                name: billingSpaceFilter.spaceCluster.label,
              },
              "",
              "",
              "",
              "",
              "Space",
              0,
              false
            );
            setSelectedSpace({
              ...smartControllersFiltersStore.selectedSpace,
            });
            setSelectedDeviceType({ ...defaultDeviceType });
            setSelectedGroupBy({ ...groupBy[0] });
            setSelectedSmartDeviceType({ ...smartDeviceTypeOptions[0] });
            setBadgeValue(0);
          }
          setShowFilters(false);
        }}
      >
        <>
          <SpaceSelectorDropdown
            dropdownOptions={[
              ...spaceClusters
                .filter(
                  (space) =>
                    space.clusterId ===
                    smartControllersFiltersStore.selectedFilterSpace.clusterId
                )
                .map((space) => {
                  return {
                    clusterId: space.clusterId,
                    id: space.id,
                    name: space.name,
                    childSpaces: space.childSpaces,
                  };
                }),
            ]}
            disabled={false}
            selectedSubSpace={selectedSpace.name}
            updateSelectedSubSpaceId={(
              selectedSubSpaceId: string,
              selectedSubSpaceName: string,
              clusterId?: string
            ) => {
              setSelectedSpace({
                id: selectedSubSpaceId,
                name: selectedSubSpaceName,
                clusterId: clusterId || "",
              });
            }}
            label="Space"
            className="font-weight-500 text-light"
          />
          <AppSelect
            label="Device Type"
            options={[...deviceTypes]}
            className="mt-4"
            selectedValue={selectedDeviceType}
            onChangeOption={(selectedDeviceTypeOption) =>
              setSelectedDeviceType(selectedDeviceTypeOption)
            }
            disabled={
              selectedGroupBy.value === "Type" ||
              selectedGroupBy.value === "Status"
            }
            fontSize="12px"
            labelFontSize="font-size-14"
            fontWeight="500"
            fontColor="#69768b"
          />
          <AppSelect
            label="Smart Controller Type"
            options={smartDeviceTypeOptions}
            className="mt-4"
            selectedValue={selectedSmartDeviceType}
            onChangeOption={(selectedSmartDeviceOption) => {
              setSelectedSmartDeviceType(selectedSmartDeviceOption);
            }}
            fontSize="12px"
            labelFontSize="font-size-14"
            fontWeight="500"
            fontColor="#69768b"
          />
          <AppSelect
            label="Group By"
            options={groupBy}
            className="mt-4"
            selectedValue={selectedGroupBy}
            onChangeOption={(selectedGroupByOption) => {
              setSelectedGroupBy(selectedGroupByOption);
            }}
            disabled={selectedDeviceType.value ? true : false}
            fontSize="12px"
            labelFontSize="font-size-14"
            fontWeight="500"
            fontColor="#69768b"
          />
        </>
      </FilteringSidePanel>
    </>
  );
};

export default SmartControllersFilter;
